<script setup lang="ts">
import { computed, ref } from 'vue'
import FbBanner from '@/components/ui/FbBanner.vue'
import { useCompletionQueueStore } from '@/stores/completionQueue'
import { useOnlineStatus } from '@/composables/useOnlineStatus'
import { createLogger } from '@/utils/logger'
import { captureError } from '@/utils/sentry'

const logger = createLogger('PendingSyncBanner')
const store = useCompletionQueueStore()
const { isOnline } = useOnlineStatus()
const busy = ref(false)

const failure = computed(() => store.failures[0])
const photoStaleItems = computed(() => store.photoStaleItems ?? [])
const stalePhoto = computed(() => photoStaleItems.value[0])
const showPending = computed(() => store.pendingCount > 0 && store.failures.length === 0 && photoStaleItems.value.length === 0)
const noun = computed(() => (store.pendingCount === 1 ? 'completion' : 'completions'))
const pendingText = computed(() => {
  if (!isOnline.value) {
    return `Saved on this device — ${store.pendingCount} ${noun.value} will sync when you're back online`
  }
  return store.isSyncing
    ? `Syncing ${store.pendingCount} ${noun.value}…`
    : `${store.pendingCount} ${noun.value} waiting to sync — will retry shortly`
})

async function submitWithoutPhoto() {
  if (busy.value || !stalePhoto.value) return
  busy.value = true
  try {
    await store.submitWithoutPhoto(stalePhoto.value.id)
  } catch (err) {
    logger.error('Failed to submit completion without photo:', err)
    captureError(err instanceof Error ? err : new Error(String(err)), {
      component: 'PendingSyncBanner', operation: 'submitWithoutPhoto'
    })
  } finally {
    busy.value = false
  }
}

async function discardQueued() {
  if (busy.value || !stalePhoto.value) return
  busy.value = true
  try {
    await store.discardQueued(stalePhoto.value.id)
  } catch (err) {
    logger.error('Failed to discard queued completion:', err)
    captureError(err instanceof Error ? err : new Error(String(err)), {
      component: 'PendingSyncBanner', operation: 'discardQueued'
    })
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <FbBanner
    v-if="showPending"
    :tone="isOnline ? 'syncing' : 'offline'"
    :icon="isOnline ? 'mdi-cloud-upload-outline' : 'mdi-wifi-off'"
    :spinner="isOnline && store.isSyncing"
    data-testid="pending-sync-banner"
  >
    {{ pendingText }}
  </FbBanner>

  <FbBanner
    v-if="photoStaleItems.length > 0"
    tone="offline"
    data-testid="pending-sync-photo-stale"
  >
    <span v-if="stalePhoto">
      Your photo for “{{ stalePhoto.choreTitle ?? 'chore' }}” is too old.
      Submit without the photo or discard this completion.
    </span>
    <template #actions>
      <v-btn
        variant="text"
        data-testid="pending-sync-photo-stale-submit"
        :disabled="busy"
        @click="submitWithoutPhoto"
      >
        Submit without photo
      </v-btn>
      <v-btn
        variant="text"
        data-testid="pending-sync-photo-stale-discard"
        :disabled="busy"
        @click="discardQueued"
      >
        Discard
      </v-btn>
    </template>
  </FbBanner>

  <FbBanner
    v-if="store.failures.length > 0"
    tone="error"
    data-testid="pending-sync-failure"
  >
    <span v-if="failure">
      Couldn't sync “{{ failure.choreTitle ?? 'chore' }}”: {{ failure.message }}
    </span>
    <template #actions>
      <v-btn
        variant="text"
        data-testid="pending-sync-failure-dismiss"
        @click="failure && store.dismissFailure(failure.id)"
      >
        Dismiss
      </v-btn>
    </template>
  </FbBanner>
</template>
